import {Color} from '@benchling/nucleus-ui';
import {Container, Box, Stack, Group, Text, Title, Center, createStyles} from '@mantine/core';
import React from 'react';

import * as OAuthDAO from 'auth/oauth/OAuthDAO';
import {AuthorizeOAuthClientRequest} from 'auth/oauth/OAuthDAO';
import {redirectTo} from 'BenchlingUtil';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

// The error response for denying consent from https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.2.1
const ACCESS_DENIED_ERROR = 'access_denied';

const useStyles = createStyles((theme) => ({
  iconContainer: {
    boxShadow: `0 2px 6px 0 ${theme.colors.gray[4]}`,
    borderRadius: '3px',
    height: '50px',
    width: '50px',
  },
  plugIcon: {
    fontSize: '25px',
    color: theme.colors.gray[7],
    transform: 'rotate(45deg)',
  },
  line: {
    width: '72px',
    border: `1px solid ${theme.colors.gray[7]}`,
  },
  arrow: {
    fontSize: '16px',
    color: theme.colors.gray[7],
    transform: 'translateX(-1px)',
  },
  logoContainer: {
    background: Color.BRAND_BIO_BLUE,
  },
  logoIcon: {
    fontSize: '36px',
    color: 'white',
  },
  scopeIcon: {
    width: theme.spacing.lg,
    color: theme.colors.gray[7],
  },
}));

interface AuthFormProps {
  children: JSX.Element;
}

function AuthForm(props: AuthFormProps): JSX.Element {
  // Mimics auth_form.jade
  return (
    <Box id="auth" className="auth-v2" h="100%">
      <Box className="container">
        <Container className="centered-box sign-in-v2 oauth-consent">{props.children}</Container>
      </Box>
    </Box>
  );
}

export interface AuthorizeConsentProps {
  clientName: string;
  currentUserEmail: string;
  request: AuthorizeOAuthClientRequest;
}

export default function AuthorizeConsent(props: AuthorizeConsentProps): JSX.Element {
  const {classes, cx} = useStyles();

  function handleCancel(): void {
    const url = new URL(props.request.redirect_uri);
    const searchParams = new URLSearchParams(url.search);
    searchParams.set('error', ACCESS_DENIED_ERROR);
    if (props.request.state) {
      searchParams.set('state', props.request.state);
    }
    url.search = searchParams.toString();

    redirectTo(url.href);
  }
  async function handleConsent(): Promise<void> {
    const {location} = await OAuthDAO.authorizeOAuthClient(props.request);
    redirectTo(location);
  }

  return (
    <AuthForm>
      <Stack spacing="lg">
        <Title order={3} ta="center">
          {props.clientName} would like access to your account
        </Title>
        <Group position="center" spacing="sm">
          <Center className={classes.iconContainer}>
            <BenchlingIcon className={classes.plugIcon} icon="plug" />
          </Center>
          <Group spacing={0}>
            <Box className={classes.line} />
            <BenchlingIcon className={classes.arrow} icon="arrow-right" />
          </Group>
          <Center className={cx(classes.iconContainer, classes.logoContainer)}>
            <BenchlingIcon className={classes.logoIcon} icon="benchling-logo" />
          </Center>
        </Group>
        <Stack spacing="lg" fz="lg">
          <Text>
            Your <b>({props.currentUserEmail})</b> consent will allow <b>{props.clientName}</b> to take these
            actions on your behalf:
          </Text>
          <Stack spacing={0} ml="md" pl="sm">
            <Group pb="xs" pt="xs">
              <BenchlingIcon className={classes.scopeIcon} icon="eye" />
              <Text>
                <b>Read:</b> On all data you have access to
              </Text>
            </Group>
            <Group pb="xs" pt="xs">
              <BenchlingIcon className={classes.scopeIcon} icon="edit" />
              <Text>
                <b>Write:</b> On all data you have access to
              </Text>
            </Group>
          </Stack>
          <Text>
            Make sure you trust <b>{props.clientName}.</b>
          </Text>
        </Stack>
        <Stack spacing="md" mt="sm" mb="sm" fz="sm" c="gray.6">
          <Text>
            By clicking "Allow," you authorize the sharing of your data between Benchling and the selected
            third party service or app ('App').
          </Text>
          <Text>
            Please note that Benchling is not responsible for the actions, content, or data handling practices
            of third party apps and services not developed, co-developed, or owned by Benchling. You assume
            all risks and liabilities associated with using any such applications and services.
          </Text>
        </Stack>
        <Group position="right" spacing="sm">
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.MEDIUM}
            onClick={handleCancel}
            text="Cancel"
          />
          <Button
            buttonStyle={ButtonStyle.PRIMARY}
            size={ButtonSize.MEDIUM}
            onClick={handleConsent}
            text="Allow"
          />
        </Group>
      </Stack>
    </AuthForm>
  );
}
